import React, { useCallback, useEffect, useState } from "react";
import { Flex, Grid } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { getPathPickProductCardGrid } from "@/features/pathpick/components/product/PathPickProductCard";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import PathPickProductCardGridSkeleton from "@/features/pathpick/components/product/PathPickProductCardGridSkeleton";
import PathPickBasketProductGridItem from "@/features/pathpick/features/basket/components/list/PathPickBasketProductGridItem";
import PathPickBasketListEmpty from "@/features/pathpick/features/basket/components/list/PathPickBasketListEmpty";
import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import PathPickRequestProductsModal from "@/features/pathpick/components/request/PathPickRequestProductsModal";
import useGetPathPickBasketProducts from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketProducts";
import usePathPickBasketFilter from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import useRemovePathPickBasketItems from "@/features/pathpick/features/basket/hooks/useRemovePathPickBasketItems";
import { PathPickProduct } from "@/features/pathpick/types";
import { toPathPickProductKey } from "@/features/pathpick/utils";

// 담은 상품 카드 리스트 — 카드 그리드
// 전체선택/선택삭제/비우기 같은 목록 전체 액션은 헤더(PathPickBasketListHeader)가,
// 페이지네이션은 sticky footer로 붙여야 해서 섹션(components/list/index)이 담당한다.
function PathPickBasketProductGrid() {
  const { t } = useI18nHelper();
  const { items, totalCount, isPending, isPlaceholderData } =
    useGetPathPickBasketProducts();
  const {
    filter,
    tab,
    hasAppliedFilters,
    pageNumber,
    pageSize,
    marketPriceBasis,
    setPageNumber,
  } = usePathPickBasketFilter();
  // 카드 보기 설정(비교 표시/배치)에 따라 카드 폭과 열 수가 달라진다.
  const { layout } = usePathPickProductCardView();
  const { selectedProducts, toggleItem, openOrderModal } =
    usePathPickBasketSelection();
  const removeItems = useRemovePathPickBasketItems();

  const [sizeStockItem, setSizeStockItem] = useState<PathPickProduct | null>(
    null
  );
  // "확인 필요" 배지로 여는 요청 상세 — 카드마다 모달을 만들지 않고 목록에서 한 번만 렌더한다.
  // sku는 모달 검색창의 초기값으로 넘겨 그 상품만 걸러진 상태로 보여준다.
  const [requestDetailTarget, setRequestDetailTarget] = useState<{
    requestId: number;
    sku: string;
  } | null>(null);

  // 카드(memo)에 넘기는 핸들러는 identity가 고정돼야 한다 — 아래 3개 모두 useCallback으로 묶는다.
  const handleDeleteItem = useCallback(
    (item: PathPickProduct) =>
      removeItems([{ id: item.id, type: item.productType }]),
    [removeItems]
  );

  const handleOrderItem = useCallback(
    (item: PathPickProduct) => openOrderModal([item]),
    [openOrderModal]
  );

  const handleOpenSizeStock = useCallback(
    (item: PathPickProduct) => setSizeStockItem(item),
    []
  );

  // 비교 기준 변경처럼 페이지를 유지하는 경로에서 결과 수가 줄면 존재하지 않는 페이지에 남아
  // 빈 목록 안내가 잘못 뜬다 — 마지막 페이지로 당긴다.
  // 로딩 중에는 이전 응답의 totalCount와 새 pageNumber를 비교하게 되므로 건너뛴다.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  useEffect(() => {
    if (isPending || isPlaceholderData) {
      return;
    }
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [isPending, isPlaceholderData, pageNumber, totalPages, setPageNumber]);

  // 응답 전에는 0건과 빈 목록을 구분할 수 없어 빈 패널이 깜빡인다 — 초기 로드는 카드 스켈레톤으로
  // 대체한다. (실제 그리드와 같은 열 수·카드 프레임이라 목록 전환 시 높이 점프가 없다)
  if (isPending) {
    return <PathPickProductCardGridSkeleton />;
  }

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      {/* 탭/페이지 전환 중에는 이전 목록을 딤드로 덮는 대신 스켈레톤으로 바꾼다.
          직전 목록 개수만큼 그려 목록 높이가 튀지 않게 한다. */}
      {isPlaceholderData ? (
        <PathPickProductCardGridSkeleton count={items.length || undefined} />
      ) : items.length === 0 ? (
        <PathPickBasketListEmpty
          tab={tab}
          hasQuery={Boolean(filter.query)}
          hasFilter={hasAppliedFilters}
        />
      ) : (
        <Grid {...getPathPickProductCardGrid(layout)}>
          {items.map((item) => {
            const key = toPathPickProductKey(item.productType, item.id);
            return (
              <PathPickBasketProductGridItem
                key={key}
                item={item}
                isSelected={selectedProducts.has(key)}
                marketPriceBasis={marketPriceBasis}
                onToggleSelect={toggleItem}
                onOpenSizeStock={handleOpenSizeStock}
                onDelete={handleDeleteItem}
                onOrder={handleOrderItem}
                onOpenRequestDetail={setRequestDetailTarget}
              />
            );
          })}
        </Grid>
      )}

      {sizeStockItem && (
        <PathPickSizeStockModal
          isOpen
          onClose={() => setSizeStockItem(null)}
          item={sizeStockItem}
        />
      )}
      {requestDetailTarget && (
        <PathPickRequestProductsModal
          isOpen
          onClose={() => setRequestDetailTarget(null)}
          requestId={requestDetailTarget.requestId}
          initialQuery={requestDetailTarget.sku}
        />
      )}
    </Flex>
  );
}

export default PathPickBasketProductGrid;
